import {
    Box,
    Grid,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/styles';
import { PaymentButton } from '@tassat/button';
import {
    AssetTypes,
    ClientWalletRoleEnum,
    OrderSide,
    PaymentActionTypes,
    PledgeTransactionTypeEnum,
    TransactionTypeEnum
} from '@tassat/common-definitions';
import { ITransactFormValues } from '@tassat/forms/src/TransactForm/types';
import { ArrowBack as BackArrowIcon } from '@tassat/icon';
import { convertToNumber } from '@tassat/utility';
import React, { useMemo } from 'react';
import ReactGA from 'react-ga4';
import { useDispatch } from 'react-redux';
import { LinkProps, Link as RouterLink } from 'react-router-dom';

import TransactionHistory from 'Containers/client/pages/transactionHistory';
import WalletDetails from 'Containers/client/pages/walletView/walletDetails';
import Header from 'Containers/header';
import { getAccountBalancesTileData } from 'Lib/accountBalancesHelper';
import history from 'Lib/browserHistory';
import { ApplicationMuiTheme } from 'Lib/themes/muiTheme';
import Worker from 'Lib/worker/PrimusWorker';
import { useAppSelector } from 'Reducers/store/hooks';
import { setSelectedAccount } from 'Reducers/ui';
import { getAccountPositionsStatsByAccountId } from 'Selectors/accountPositions';
import { getLinkedDBAccountByEOAAccountId } from 'Selectors/accounts';
import { getAllAssetsArrayWithPrice, getAllSecurityCurrencyPairs } from 'Selectors/assets';
import { getSelectedAccountId } from 'Selectors/common';
import { getKillSwitchOn } from 'Selectors/killswitch';
import { getMappedUserAccountByUserAndAccountId } from 'Selectors/userAccounts';
import { PledgeResultModal, TransactionModal } from 'SharedComponents/modals';
import { ModalPledgeValues } from 'SharedComponents/modals/pledgeDetailsModal/pledgeContextWorkflow';

import CreatePledge from '../../overlays/createPledge';
import { ICreatePledgeFormValues } from '../../overlays/createPledge/createPledgeForm/types';
import SendPayment from '../../overlays/send';
import { ISendFormValues } from '../../overlays/send/sendForm/types';
import TransactPayment from '../../overlays/transact';
import Withdrawal from '../../overlays/withdrawal';
import { IWithdrawalFormValues } from '../../overlays/withdrawal/withdrawalForm/types';

import type { IWalletViewProps } from '.';

export const PAGE_TITLE = 'Firms';

const Link = (RouterLink as unknown) as React.ComponentClass<LinkProps>;

export function WalletView(props: IWalletViewProps) {
    const {
        classes,
        location,
        match
    } = props;

    const selectedAccountId = useAppSelector(getSelectedAccountId);
    const accountId = match.params.walletId;
    const killSwitch = useAppSelector(getKillSwitchOn);
    const userAccountMapped = useAppSelector((state) => getMappedUserAccountByUserAndAccountId(state, state.user?.UserId ?? '', accountId));
    const linkedBDAccountId = useAppSelector((state) => getLinkedDBAccountByEOAAccountId(state, accountId));
    const accountPositionBalance = useAppSelector((state) => getAccountPositionsStatsByAccountId(state, accountId));
    const allSecurityCurrencyPairs = useAppSelector(getAllSecurityCurrencyPairs);
    const assetPair = allSecurityCurrencyPairs[0] ?? {};
    const allAssets = useAppSelector((state) => getAllAssetsArrayWithPrice(state));

    const accountPositionStats = useMemo(() => {
        if (accountPositionBalance && accountPositionBalance[0] &&
            Object.keys(accountPositionBalance[0].ccy).length === 0 &&
            Object.keys(accountPositionBalance[0].security).length === 0) return [];

        return getAccountBalancesTileData((accountPositionBalance || []), userAccountMapped?.firm?.IsCollateral);
    }, [ accountPositionBalance, userAccountMapped ]);

    const theme = useTheme<ApplicationMuiTheme>();
    const dispatch = useDispatch();

    React.useEffect(() => {
        return () => {
            location.state = undefined;
            dispatch(setSelectedAccount(''));
        };
    }, []);

    React.useEffect(() => {
        if (!userAccountMapped) return;

        if (!userAccountMapped.account || (userAccountMapped.account && userAccountMapped.account.IsClosed)) {
            history.push('/accounts');
        }

        if (userAccountMapped.account && userAccountMapped.account.AccountId !== selectedAccountId) {
            dispatch(setSelectedAccount(userAccountMapped.account.AccountId));
        }
    }, [ userAccountMapped, selectedAccountId, dispatch ]);

    // payment actions and overlay displays
    const [ transactionValues, setTransactionValues ] = React.useState<ITransactFormValues | ISendFormValues |
        ICreatePledgeFormValues | IWithdrawalFormValues>(null);

    const [ transactionInProgress, setTransactionInProgress ] = React.useState<boolean>(false);
    const [ pledgeInProgress, setPledgeInProgress ] = React.useState<boolean>(false);

    const showPaymentButtons = React.useMemo(() => {
        return userAccountMapped?.userAccount?.ClientRoleId !== ClientWalletRoleEnum.VIEW_ONLY &&
            userAccountMapped?.user && !userAccountMapped?.user.IsLocked && !userAccountMapped?.user.IsFrozen &&
            userAccountMapped?.firm && !userAccountMapped?.firm.IsFrozen && !userAccountMapped?.firm.IsLocked &&
            userAccountMapped?.account && !userAccountMapped?.account.IsLocked && !userAccountMapped?.account.IsClosed;
    }, [
        userAccountMapped?.userAccount,
        userAccountMapped?.user,
        userAccountMapped?.firm,
        userAccountMapped?.account
    ]);

    const [ sendOverlayOpen, setSendOverlayOpen ] = React.useState<boolean>(false);
    const [ createPledgeOverlayOpen, setCreatePledgeOverlayOpen ] = React.useState<boolean>(false);
    const [ withdrawalOverlayOpen, setwithdrawalOverlayOpen ] = React.useState<boolean>(false);

    const withdrawalActionScreen = (withdrawalInitialValues?: ITransactFormValues) => {
        if (!withdrawalOverlayOpen) {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'withdraw_button_click'
            });
            if (withdrawalInitialValues?.Type === TransactionTypeEnum.withdrawal) {
                setTransactionValues(withdrawalInitialValues);
            } else {
                setTransactionValues(null);
            }
        }
        setwithdrawalOverlayOpen(!withdrawalOverlayOpen);
    };

    const sendActionScreen = (sendInitialValues?: ISendFormValues) => {
        if (!sendOverlayOpen) {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'send_button_click'
            });
            if (sendInitialValues?.Type === TransactionTypeEnum.send) {
                setTransactionValues(sendInitialValues);
            } else {
                setTransactionValues(null);
            }
        }
        setSendOverlayOpen(!sendOverlayOpen);
    };

    const handleSend = (sendValues: ISendFormValues) => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'send_submit'
        });
        let sendRequestValues: any = {
            UserId: user.UserId,
            AssetId: sendValues.AssetType.refData.security?.AssetId ?? sendValues.AssetType.refData.currency.AssetId,
            CurCode: sendValues.AssetType.refData.security?.Curcode ?? sendValues.AssetType.refData.currency.Curcode,
            SenderFirmId: sendValues.Sender.FirmId,
            SenderAccountId: sendValues.Sender.AccountId,
            RecipientAccount: sendValues.RecipientAddress ? sendValues.RecipientAddress : sendValues.Recipient.value.Account,
            PrivateDescription: sendValues.PrivateDescription,
            PublicDescription: sendValues.PublicDescription,
            Purpose: sendValues.Purpose.value,
            Ticker: sendValues.AssetType.refData.security?.Ticker ?? sendValues.AssetType.refData.currency.Ticker
        };

        if (sendValues.isFiatAmount) {
            sendRequestValues = {
                ...sendRequestValues,
                FiatAmount: convertToNumber(sendValues.FiatAmount)
            };
        } else
            sendRequestValues = {
                ...sendRequestValues,
                AssetAmount: sendValues.AssetAmount ? convertToNumber(sendValues.AssetAmount)
                    : convertToNumber(sendValues.FiatAmount)
            };

        if (sendValues.AssetType.refData?.security?.Type === AssetTypes.ASSET) {
            sendRequestValues = {
                ...sendRequestValues,
                RequestPrice: sendValues.AssetType.refData.security.Price
            };
        }

        setTransactionInProgress(true);
        sendActionScreen();

        Worker.getInstance()
            .createSendRequest(sendRequestValues);
    };

    const createPledgeActionScreen = (createPledgeInitialValues?: ICreatePledgeFormValues) => {
        if (!createPledgeOverlayOpen) {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'createPledge_button_click'
            });
            if (createPledgeInitialValues?.Type === TransactionTypeEnum.new_pledge) {
                setTransactionValues(createPledgeInitialValues);
            } else {
                setTransactionValues(null);
            }
        }
        setCreatePledgeOverlayOpen(!createPledgeOverlayOpen);
    };

    const handleCreatePledge = (createPledgeValues: ICreatePledgeFormValues) => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'createPledge_submit'
        });
        const createPledgeRequestValues = {
            UserId: user.UserId,
            PledgorAccount: createPledgeValues.Sender.Account,
            Pledgee: [
                {
                    PledgeeAccount: createPledgeValues.RecipientAddress ?
                        createPledgeValues.RecipientAddress : createPledgeValues?.Recipient?.value.Account
                }
            ],
            PrivateDescription: createPledgeValues.PrivateDescription,
            PublicDescription: createPledgeValues.PublicDescription,
            Ticker: createPledgeValues.AssetType.refData.security?.Ticker ?? createPledgeValues.AssetType.refData.currency?.Ticker,
            AssetQuantity: createPledgeValues.AssetAmount ? convertToNumber(createPledgeValues.AssetAmount) : 0,
            ApprovalNeeded: userAccountMapped?.userAccount?.ClientRoleId === ClientWalletRoleEnum.MAKER ||
                userAccountMapped?.userAccount?.ClientRoleId === ClientWalletRoleEnum.APPROVER
        };

        setPledgeInProgress(true);
        createPledgeActionScreen();

        Worker.getInstance()
            .createPledgeRequest(createPledgeRequestValues);
    };

    const handleWithdrawal = (withdrawValues: IWithdrawalFormValues) => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'withdraw_submit'
        });
        const withdrawalRequestValues = {
            UserId: user.UserId,
            FirmId: user.FirmId,
            Account: userAccountMapped.account.Account,
            AccountWithdrawalDataId: withdrawValues.WithdrawalInstructions.refData.AccountWithdrawalDataId,
            WithdrawalAmount: convertToNumber(withdrawValues.WithdrawalAmount),
            PrivateDescription: withdrawValues.PrivateDescription.trim(),
            RequestType: withdrawValues.Type,
            AssetId: assetPair.currency?.AssetId
        };

        setTransactionInProgress(true);
        withdrawalActionScreen();

        Worker.getInstance()
            .createWithdrawalRequest(withdrawalRequestValues);
    };

    const [ transactOverlayOpen, settransactOverlayOpen ] = React.useState<boolean>(false);
    const transactActionScreen = () => {
        if (!transactOverlayOpen) {
            ReactGA.event('click', {
                event_category: 'engagement',
                event_label: 'transaction_button_click'
            });
            setTransactionValues(null);
        }
        settransactOverlayOpen(!transactOverlayOpen);
    };
    const handleTransact = (transactValues: ITransactFormValues) => {
        ReactGA.event('click', {
            event_category: 'engagement',
            event_label: 'transaction_submit'
        });
        let transactRequest;

        const selectedAssetType = allAssets?.find(asset => asset.AssetId === transactValues.AssetType.value);

        if (OrderSide.SUBSCRIPTION === transactValues.Type || OrderSide.BUY === transactValues.Type) {
            transactRequest = {
                UserId: user.UserId,
                AssetId: selectedAssetType?.AssetId,
                Amount: transactValues.Amount,
                FeeAmount: transactValues.Fee,
                FirmId: transactValues.Wallet.FirmId,
                AccountId: transactValues.Wallet.AccountId,
                PrivateDescription: transactValues.PrivateDescription.trim(),
                OrderType: 'L',
                Side: transactValues.Type,
                Ticker: selectedAssetType?.Ticker
            };
        }
        if (OrderSide.REDEEM === transactValues.Type || OrderSide.SELL === transactValues.Type) {
            transactRequest = {
                UserId: user.UserId,
                AssetId: selectedAssetType?.AssetId,
                Quantity: transactValues.Amount,
                FeeAmount: transactValues.Fee,
                FirmId: transactValues.Wallet.FirmId,
                AccountId: transactValues.Wallet.AccountId,
                PrivateDescription: transactValues.PrivateDescription.trim(),
                OrderType: 'L',
                Side: transactValues.Type,
                Ticker: selectedAssetType?.Ticker
            };
        }

        const NeedsApproval = false;

        setTransactionInProgress(true);
        transactActionScreen();
        Worker.getInstance()
            .createTransactRequest(transactRequest, NeedsApproval);
    };

    React.useEffect(() => {
        if (killSwitch && transactOverlayOpen) {
            settransactOverlayOpen(false);
        }
    }, [ killSwitch, transactOverlayOpen ]);

    if (!userAccountMapped) return <div />;

    const {
        user,
        userAccount,
        firm,
        account
    } = userAccountMapped;

    const pledgeTransactionValues = transactionValues as ICreatePledgeFormValues;
    const modalPledgeValues: ModalPledgeValues | null = transactionValues ? {
        Type: pledgeTransactionValues.Type,
        AssetType: pledgeTransactionValues.AssetType,
        AssetAmount: pledgeTransactionValues.AssetAmount,
        FiatAmount: pledgeTransactionValues.FiatAmount,
        Recipient: pledgeTransactionValues.Recipient,
        RecipientAddress: pledgeTransactionValues.RecipientAddress
    } : null;

    return (
        <Grid
            className={classes.root}
            container
            direction='column'
            spacing={0}
            wrap='nowrap'
        >
            <Header />
            <Grid
                className={classes.topContent}
                container
                direction='row'
                item
                spacing={0}
                xs={12}
            >
                <Grid
                    className={classes.backArrow}
                    item
                >
                    <Grid
                        container
                        direction='row'
                        item
                        justifyContent='flex-start'
                        sm={12}
                        spacing={0}
                    >
                        <Link
                            className={classes.anchorText}
                            to='/accounts'
                        >
                            <BackArrowIcon
                                className={classes.itemTitle}
                                iconColor={theme?.app?.applicationColors.fontColor ||
                                    '#202020'}
                            />
                        </Link>
                    </Grid>
                </Grid>
                <Grid
                    container
                    item
                    justifyContent='flex-start'
                    xs={7}
                >
                    <WalletDetails
                        accountPositionStats={accountPositionStats}
                        linkedBDAccountId={linkedBDAccountId}
                        userAccountMapped={userAccountMapped}
                    />
                </Grid>
                <Grid
                    item
                    xs={4}
                >
                    {showPaymentButtons &&
                        <Grid
                            className={classes.actionButtonContainer}
                            container
                            direction='row'
                            item
                            justifyContent='flex-end'
                            xs={12}
                        >
                            {!killSwitch &&
                                <>
                                    <Box className={classes.actionButton}>
                                        <PaymentButton
                                            changeActionScreen={transactActionScreen}
                                            isWalletFrozen={false}
                                            type='transact'
                                        />
                                    </Box>
                                    <Box className={classes.actionButton}>
                                        <PaymentButton
                                            changeActionScreen={sendActionScreen}
                                            isWalletFrozen={false}
                                            type='send'
                                        />
                                    </Box>
                                    <Box className={classes.actionButton}>
                                        <PaymentButton
                                            changeActionScreen={withdrawalActionScreen}
                                            isWalletFrozen={false}
                                            type='withdraw'
                                        />
                                    </Box>
                                    {userAccountMapped?.firm?.IsCollateral &&
                                        <Box className={classes.actionButton}>
                                            <PaymentButton
                                                changeActionScreen={createPledgeActionScreen}
                                                isWalletFrozen={false}
                                                text='post'
                                                type='createPledge'
                                            />
                                        </Box>}
                                </>}
                        </Grid>}
                </Grid>
            </Grid>
            {killSwitch &&
                <Grid
                    container
                    direction='row'
                    justifyContent='center'
                    spacing={0}
                    wrap='nowrap'
                >
                    <Typography
                        className={classes.killSwitchMessage}
                        variant='body1'
                    >Platform features are temporarily unavailable.
                    </Typography>
                </Grid>}
            <Grid
                className={classes.tabContent}
                item
                xs={12}
            >
                <TransactionHistory
                    account={account}
                    firm={firm}
                    overlayOpen={createPledgeOverlayOpen || sendOverlayOpen || transactOverlayOpen ||
                        withdrawalOverlayOpen || transactionInProgress}
                />
            </Grid>
            {sendOverlayOpen && (
                <SendPayment
                    accountPositionBalance={accountPositionBalance}
                    goBack={sendActionScreen}
                    handleSend={handleSend}
                    setTransactionValues={setTransactionValues}
                    title={PaymentActionTypes['send'].displayText}
                    transactionValues={(transactionValues as ISendFormValues)}
                />
            )}
            {transactOverlayOpen && (
                <TransactPayment
                    accountPositionBalance={accountPositionBalance}
                    allAssets={allAssets}
                    goBack={transactActionScreen}
                    handleTransact={handleTransact}
                    setTransactionValues={setTransactionValues}
                    title={PaymentActionTypes['transact'].displayText}
                    transactionValues={(transactionValues as ITransactFormValues)}
                />
            )}
            {withdrawalOverlayOpen && (
                <Withdrawal
                    accountPositionBalance={accountPositionBalance}
                    goBack={withdrawalActionScreen}
                    handleWithdrawal={handleWithdrawal}
                    setTransactionValues={setTransactionValues}
                    title={PaymentActionTypes['withdrawal'].displayText}
                    transactionValues={(transactionValues as IWithdrawalFormValues)}
                />
            )}
            {createPledgeOverlayOpen && (
                <CreatePledge
                    accountPositionBalance={accountPositionBalance}
                    goBack={createPledgeActionScreen}
                    handleCreatePledge={handleCreatePledge}
                    setTransactionValues={setTransactionValues}
                    title={PaymentActionTypes['createPledge'].displayText}
                    transactionValues={(transactionValues as ICreatePledgeFormValues)}
                />
            )}
            <TransactionModal
                allAssets={allAssets}
                onClose={() => {
                    setTransactionInProgress(false);
                    dispatch({ type: 'RESET_VALIDATION' });
                    setTransactionValues(null);
                }}
                open={transactionInProgress}
                transactionValues={transactionValues}
            />
            <PledgeResultModal
                modalPledgeValues={modalPledgeValues}
                onClose={() => {
                    setPledgeInProgress(false);
                    dispatch({ type: 'RESET_VALIDATION' });
                    setTransactionValues(null);
                }}
                open={pledgeInProgress}
                pledgeType={PledgeTransactionTypeEnum.new_pledge}
                pledgeValidationType='pledgeCollateral'
            />
        </Grid>
    );
}